import React, { useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useUserStore from "../../stores/userStore";
import { CustomTypography } from "../custom-typography/custom-typography";
import { AvatarWithCircle } from "./user-avatar/avatar-with-circle";
import useAppStore from "../../stores/appStore";

interface UserMenuTriggerProps {
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  isMenuOpen?: boolean;
  isStatistic?: boolean;
}

const LOGIN_MAX_LENGTH = 16;

const truncateLogin = (value: string, maxLength = LOGIN_MAX_LENGTH) =>
  value.length > maxLength ? `${value.slice(0, maxLength)}...` : value;

export const UserMenuTrigger: React.FC<UserMenuTriggerProps> = ({
  onClick,
  isMenuOpen,
  isStatistic = false,
}) => {
  const theme = useTheme();
  const { user } = useUserStore();
  const { userAvatar } = useAppStore();
  const [localAnimationTrigger, setLocalAnimationTrigger] = useState(0);
  const prevMenuOpenRef = useRef(false);

  useEffect(() => {
    if (isMenuOpen && !prevMenuOpenRef.current) {
      setLocalAnimationTrigger((prev) => prev + 1);
    }
    prevMenuOpenRef.current = isMenuOpen || false;
  }, [isMenuOpen]);

  const textEllipsisSx = {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  } as const;

  return (
    <Box display="flex" alignItems="center" gap={2} sx={{ minWidth: 0, width: "100%" }}>
      <Box
        onClick={onClick}
        display="flex"
        alignItems="center"
        sx={{
          cursor: isStatistic ? "default" : "pointer",
          minWidth: 0,
          width: "100%",
        }}
      >
        <AvatarWithCircle
          img={userAvatar || undefined}
          size="40px"
          userId={user.id}
          animationTrigger={localAnimationTrigger}
          useGlobalTrigger={true}
        />
        <Box
          display="flex"
          alignItems="center"
          gap={1}
          ml={{ xs: 2.5, sm: 3 }}
          sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}
        >
          <Box sx={{ minWidth: 0, overflow: "hidden" }}>
            <CustomTypography
              sx={{
                fontWeight: 700,
                fontSize: { xs: 12, sm: 14 },
                color: isStatistic
                  ? "#FFFFFF"
                  : theme.palette.header.userBlock.nameColor,
                ...textEllipsisSx,
              }}
            >
              {truncateLogin(user.first_name)}
            </CustomTypography>
            <CustomTypography
              sx={{
                fontWeight: 500,
                fontSize: { xs: 11, sm: 12 },
                color: isStatistic
                  ? "#FFFFFF99"
                  : theme.palette.header.userBlock.linkColor,
                ...textEllipsisSx,
              }}
            >
              @{truncateLogin(user.username)}
            </CustomTypography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
